import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  adminApprove,
  adminGetReview,
  adminListReviews,
  adminReject,
  adminRequestRevision,
} from "@/lib/afritask";

const TYPES = [
  { value: "all", label: "All" },
  { value: "text_classification", label: "Text" },
  { value: "image_labeling", label: "Image" },
  { value: "translation", label: "Translation" },
  { value: "audio_transcription", label: "Audio" },
  { value: "prompt_rating", label: "Rating" },
];

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const previewOf = (s) => {
  const str = JSON.stringify(s || {});
  return str.length > 60 ? str.slice(0, 60) + "…" : str;
};

export default function AdminReviews() {
  const [type, setType] = useState("all");
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(null); // full review object
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [payoutPoints, setPayoutPoints] = useState(0);
  const [rejectReason, setRejectReason] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await adminListReviews(type);
      setEntries(d.entries);
    } catch {
      /* ignore */
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (entry_id) => {
    try {
      const d = await adminGetReview(entry_id);
      setSelected(d);
      setPayoutPoints(d.task?.reward_points || 0);
      setRejectReason("");
      setRevisionNote("");
      setDrawerOpen(true);
    } catch {
      toast.error("Failed to load submission");
    }
  };

  const approve = async () => {
    try {
      setBusy(true);
      const r = await adminApprove(selected.entry.entry_id, payoutPoints);
      toast.success(`Approved — ₦${r.payout_ngn.toLocaleString()} paid`);
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      setBusy(true);
      await adminReject(selected.entry.entry_id, rejectReason);
      toast.success("Rejected");
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const revise = async () => {
    if (!revisionNote.trim()) {
      toast.error("Note is required");
      return;
    }
    try {
      setBusy(true);
      await adminRequestRevision(selected.entry.entry_id, revisionNote);
      toast.success("Revision requested");
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs value={type} onValueChange={setType}>
        <TabsList className="flex-wrap">
          {TYPES.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`filter-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-reviews-table">
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-sm text-neutral-500">
                    Queue is empty. All submissions reviewed 🎉
                  </TableCell>
                </TableRow>
              )}
              {entries.map((e) => (
                <TableRow key={e.entry_id}>
                  <TableCell className="text-xs">{fmtWhen(e.submitted_at)}</TableCell>
                  <TableCell className="font-medium">{e.task_title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {e.task_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.username}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-neutral-500">
                    {previewOf(e.submission_data)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => open(e.entry_id)}
                      data-testid={`view-btn-${e.entry_id}`}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="max-w-2xl" data-testid="review-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.task?.title}
                  <Badge variant="outline">{selected.task?.task_type}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">
                    Task brief
                  </p>
                  <p className="mt-1 rounded-md bg-neutral-50 p-3 text-sm">
                    {selected.task?.task_payload?.description ||
                      selected.task?.description}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">
                    Submission by {selected.user?.username}
                  </p>
                  <pre
                    className="mt-1 max-h-64 overflow-auto rounded-md bg-neutral-900 p-3 text-xs text-emerald-300"
                    data-testid="submission-json"
                  >
                    {JSON.stringify(selected.entry?.submission_data, null, 2)}
                  </pre>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="text-xs font-semibold">Payout (points)</label>
                    <Input
                      type="number"
                      min={0}
                      value={payoutPoints}
                      onChange={(e) => setPayoutPoints(parseInt(e.target.value || "0", 10))}
                      data-testid="payout-input"
                    />
                    <p className="mt-1 text-[10px] text-neutral-500">
                      = ₦{(payoutPoints * 10).toLocaleString()}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold">Reject reason / revision note</label>
                    <Textarea
                      value={rejectReason || revisionNote}
                      onChange={(e) => {
                        setRejectReason(e.target.value);
                        setRevisionNote(e.target.value);
                      }}
                      rows={2}
                      placeholder="Required for reject / revision"
                      data-testid="reason-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={revise}
                  disabled={busy}
                  data-testid="revise-btn"
                >
                  Request revision
                </Button>
                <Button
                  variant="destructive"
                  onClick={reject}
                  disabled={busy}
                  data-testid="reject-btn"
                >
                  Reject
                </Button>
                <Button
                  onClick={approve}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="approve-btn"
                >
                  Approve · ₦{(payoutPoints * 10).toLocaleString()}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
